import React, { useState } from 'react';
import { Box, Typography, useTheme, Button, Menu, MenuItem } from '@mui/material';
import { useThemeContext } from './theme/ThemeContext';
import BasicModal from './modais/BasicModal';
import ReactPlayer from 'react-player';
import { Info, PlayCircle } from '@mui/icons-material';

interface InfoHeaderProps {
  title: string;
  message: string;
  isImportant: boolean;
  url?: string;
  modalTitle?: string;
  urlTutorial?: string;
  urlConceito?: string;
}

const InfoHeader: React.FC<InfoHeaderProps> = ({ 
  title, 
  message, 
  isImportant, 
  url, 
  modalTitle,
  urlTutorial,
  urlConceito 
}) => {
  const theme = useTheme();
  const { toggleTheme, mode } = useThemeContext();
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string>('');
  const [currentVideoTitle, setCurrentVideoTitle] = useState<string>('');

  const hasMultipleVideos = urlTutorial && urlConceito;
  const hasSingleVideo = url && modalTitle;

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleVideoSelect = (videoUrl: string, videoTitle: string) => {
    setCurrentVideoUrl(videoUrl);
    const title = modalTitle ? `${modalTitle} - ${videoTitle}` : videoTitle;
    setCurrentVideoTitle(title);
    setIsVideoModalOpen(true);
    handleMenuClose();
  };

  const handleSingleVideoClick = () => {
    if (url && modalTitle) {
      setCurrentVideoUrl(url);
      setCurrentVideoTitle(modalTitle);
      setIsVideoModalOpen(true);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: '100%',
        padding: '0px 0px 20px 0px',
        margin: theme.spacing(1, 0),
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: 'bold',
            textAlign: 'left',
            marginBottom: theme.spacing(1),
            color: mode === "light" ? theme.palette.primary.main : '#fff'
          }}
        >
          {title}
        </Typography>
        {(hasMultipleVideos || hasSingleVideo) && (
          <>
            <Button
              variant="contained"
              sx={{
                height: 30,
                minWidth: 0,
                paddingInline: '8px',
                paddingBlock: '12px',
                borderRadius: 1.8
              }}
              onClick={hasMultipleVideos ? handleMenuClick : handleSingleVideoClick}
            >
              <PlayCircle sx={{ fontSize: 16 }} />
            </Button>
            
            {hasMultipleVideos && (
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <MenuItem onClick={() => handleVideoSelect(urlTutorial, 'Tutorial')}>
                  Tutorial
                </MenuItem>
                <MenuItem onClick={() => handleVideoSelect(urlConceito, 'Conceito')}>
                  Conceito
                </MenuItem>
              </Menu>
            )}
          </>
        )}
      </Box>
      <Typography
        component="p"
        sx={{
          textAlign: 'left',
          fontSize: theme.typography.body1.fontSize,
          lineHeight: 1.6,
          color: theme.palette.text.primary,
        }}
      >
        {isImportant && (
          <span style={{
            color: theme.palette.error.main,
            fontWeight: 'bold',
            marginRight: theme.spacing(0.5)
          }}>
            Importante:
          </span>
        )}
        {message}
      </Typography>

      {(hasMultipleVideos || hasSingleVideo) && (
        <BasicModal
          title={currentVideoTitle}
          open={isVideoModalOpen}
          onClose={() => setIsVideoModalOpen(false)}
          sizeContent="medium"
          tela='free-access'
        >
          <Box sx={{ position: 'relative', paddingTop: '56.2%' }}>
            <ReactPlayer
              url={currentVideoUrl}
              width='100%'
              height='100%'
              style={{ position: 'absolute', top: 0, left: 0 }}
              controls
            />
          </Box>
        </BasicModal>
      )}
    </Box>
  );
};

export default InfoHeader;